'use client';

import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X, XCircle } from 'lucide-react';

// ════════════════════════════════════════════
// Types
// ════════════════════════════════════════════

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  duration: number;
  createdAt: number;
}

interface ToastContextType {
  toast: (message: string, type?: ToastType, title?: string, duration?: number) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// ════════════════════════════════════════════
// Toast Item Component
// ════════════════════════════════════════════

const toastConfig: Record<ToastType, { icon: typeof CheckCircle2; color: string; bg: string; border: string; bar: string }> = {
  success: { icon: CheckCircle2,   color: 'text-green-500',  bg: 'bg-[#121212]', border: 'border-green-500/30', bar: 'bg-green-500' },
  error:   { icon: XCircle,        color: 'text-red-500',    bg: 'bg-[#121212]', border: 'border-red-500/30',   bar: 'bg-red-500' },
  warning: { icon: AlertTriangle,  color: 'text-yellow-500', bg: 'bg-[#121212]', border: 'border-yellow-500/30', bar: 'bg-yellow-500' },
  info:    { icon: Info,           color: 'text-blue-500',   bg: 'bg-[#121212]', border: 'border-blue-500/30',  bar: 'bg-blue-500' },
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef(Date.now());
  const remainingRef = useRef(toast.duration);

  const config = toastConfig[toast.type];
  const Icon = config.icon;

  const dismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 280);
  }, [onDismiss, toast.id]);

  // Progress & auto-dismiss
  useEffect(() => {
    if (isPaused) return;

    startTimeRef.current = Date.now();
    const startRemaining = remainingRef.current;

    const updateProgress = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const newRemaining = startRemaining - elapsed;
      remainingRef.current = newRemaining;

      if (newRemaining <= 0) {
        dismiss();
        return;
      }

      setProgress((newRemaining / toast.duration) * 100);
      timerRef.current = setTimeout(updateProgress, 30);
    };

    timerRef.current = setTimeout(updateProgress, 30);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isPaused, dismiss, toast.duration]);

  return (
    <div
      className={`
        relative overflow-hidden
        w-[340px] max-w-[90vw]
        rounded-[var(--radius)] border backdrop-blur-xl
        ${config.bg} ${config.border}
        shadow-2xl shadow-black/30
        ${isExiting ? 'toast-exit' : 'toast-enter'}
      `}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      role="alert"
    >
      <div className="flex items-start gap-3 px-4 py-3">
        {/* Icon */}
        <div className={`mt-[3px] shrink-0 ${config.color}`}>
          <Icon size={18} strokeWidth={2.5} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {toast.title && (
            <p className="text-[13px] font-700 text-[var(--foreground)] leading-tight">{toast.title}</p>
          )}
          <p className={`text-[12px] text-[var(--muted-foreground)] leading-relaxed ${toast.title ? 'mt-0.5' : ''}`}>
            {toast.message}
          </p>
        </div>

        {/* Close */}
        <button
          onClick={dismiss}
          className="shrink-0 mt-0.5 p-0.5 rounded hover:bg-white/10 text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
        >
          <X size={13} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-[2px] w-full bg-white/[0.03]">
        <div
          className={`h-full ${config.bar} opacity-40 transition-none`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════
// Toast Provider
// ════════════════════════════════════════════

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counterRef = useRef(0);

  const addToast = useCallback((message: string, type: ToastType = 'info', title?: string, duration = 4500) => {
    counterRef.current += 1;
    const id = `toast-${counterRef.current}-${Date.now()}`;

    setToasts(prev => {
      // Max 5 toasts visible
      const limited = prev.length >= 5 ? prev.slice(1) : prev;
      return [...limited, { id, type, message, title, duration, createdAt: Date.now() }];
    });
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const value: ToastContextType = {
    toast: addToast,
    success: (msg, title) => addToast(msg, 'success', title),
    error: (msg, title) => addToast(msg, 'error', title),
    warning: (msg, title) => addToast(msg, 'warning', title),
    info: (msg, title) => addToast(msg, 'info', title),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast Container — bottom-right */}
      {toasts.length > 0 && (
        <div className="fixed bottom-6 right-6 z-[9998] flex flex-col-reverse gap-2 pointer-events-none">
          {toasts.map(t => (
            <div key={t.id} className="pointer-events-auto">
              <ToastItem toast={t} onDismiss={removeToast} />
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}
